import React, { useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Upload, Loader2 } from 'lucide-react';
import emailjs from '@emailjs/browser';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const Apply = () => {
  const { position } = useParams();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const [formData, setFormData] = useState({
    from_name: '',
    last_name: '',
    reply_to: '',
    phone: '',
    location: '',
    experience: '',
    portfolio: '',
    linkedin: '',
    github: '',
    cover_letter: '',
    resume_link: '',
    availability: '',
    salary: '',
    referral: '',
    position: position || '', // Initialize with the position from URL params
    message: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Format the message with all application details
      const messageDetails = `
Position Applied For: ${position}

Personal Information:
- Name: ${formData.from_name} ${formData.last_name}
- Email: ${formData.reply_to}
- Phone: ${formData.phone}
- Location: ${formData.location}

Professional Information:
- Experience: ${formData.experience}
- Portfolio: ${formData.portfolio}
- LinkedIn: ${formData.linkedin}
- GitHub: ${formData.github}
- Resume Link: ${formData.resume_link}

Additional Information:
- Availability: ${formData.availability}
- Expected Salary: ${formData.salary}
- Referral Source: ${formData.referral}

Cover Letter:
${formData.cover_letter}
`;

      // Update formData with the formatted message
      setFormData(prev => ({
        ...prev,
        message: messageDetails
      }));

      const result = await emailjs.sendForm(
        'service_l7n36yv',
        'template_b8gzsad',
        formRef.current!,
        '3pGgUTaif-_dIInLH'
      );

      if (result.text === 'OK') {
        toast.success('Application submitted successfully!');
        setTimeout(() => {
          navigate('/careers');
        }, 2000);
      }
    } catch (error) {
      toast.error('Error submitting application. Please try again.');
      console.error('Error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="bg-white min-h-screen">
      <ToastContainer position="bottom-right" />
      
      {/* Hero Section */}
      <section className="bg-slate-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl sm:text-4xl font-bold mb-4">Apply for {position}</h1>
          <p className="text-slate-300 text-lg">Join our team and help build the future of automation and trading technology</p>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <form ref={formRef} onSubmit={handleSubmit} className="space-y-8">
            {/* Hidden fields for EmailJS */}
            <input
              type="hidden"
              name="position"
              value={position || ''}
            />
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              className="hidden"
            />
            
            {/* Personal Information */}
            <div className="bg-white p-6 rounded-lg shadow-lg border border-slate-200">
              <h2 className="text-xl font-semibold mb-6">Personal Information</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="from_name" className="block text-sm font-medium text-slate-700 mb-1">
                    First Name *
                  </label>
                  <input
                    type="text"
                    id="from_name"
                    name="from_name"
                    required
                    value={formData.from_name}
                    onChange={handleChange}
                    className="w-full rounded-md border-slate-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label htmlFor="last_name" className="block text-sm font-medium text-slate-700 mb-1">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    id="last_name"
                    name="last_name"
                    required
                    value={formData.last_name}
                    onChange={handleChange}
                    className="w-full rounded-md border-slate-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label htmlFor="reply_to" className="block text-sm font-medium text-slate-700 mb-1">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="reply_to"
                    name="reply_to"
                    required
                    value={formData.reply_to}
                    onChange={handleChange}
                    className="w-full rounded-md border-slate-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-slate-700 mb-1">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full rounded-md border-slate-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label htmlFor="location" className="block text-sm font-medium text-slate-700 mb-1">
                    Current Location *
                  </label>
                  <input
                    type="text"
                    id="location"
                    name="location"
                    required
                    value={formData.location}
                    onChange={handleChange}
                    className="w-full rounded-md border-slate-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Professional Information */}
            <div className="bg-white p-6 rounded-lg shadow-lg border border-slate-200">
              <h2 className="text-xl font-semibold mb-6">Professional Information</h2>
              <div className="space-y-6">
                <div>
                  <label htmlFor="experience" className="block text-sm font-medium text-slate-700 mb-1">
                    Years of Experience *
                  </label>
                  <select
                    id="experience"
                    name="experience"
                    required
                    value={formData.experience}
                    onChange={handleChange}
                    className="w-full rounded-md border-slate-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  >
                    <option value="">Select experience</option>
                    <option value="0-1">0-1 years</option>
                    <option value="1-3">1-3 years</option>
                    <option value="3-5">3-5 years</option>
                    <option value="5-10">5-10 years</option>
                    <option value="10+">10+ years</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="portfolio" className="block text-sm font-medium text-slate-700 mb-1">
                    Portfolio URL
                  </label>
                  <input
                    type="url"
                    id="portfolio"
                    name="portfolio"
                    value={formData.portfolio}
                    onChange={handleChange}
                    className="w-full rounded-md border-slate-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label htmlFor="linkedin" className="block text-sm font-medium text-slate-700 mb-1">
                    LinkedIn Profile
                  </label>
                  <input
                    type="url"
                    id="linkedin"
                    name="linkedin"
                    value={formData.linkedin}
                    onChange={handleChange}
                    className="w-full rounded-md border-slate-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label htmlFor="github" className="block text-sm font-medium text-slate-700 mb-1">
                    GitHub Profile
                  </label>
                  <input
                    type="url"
                    id="github"
                    name="github"
                    value={formData.github}
                    onChange={handleChange}
                    className="w-full rounded-md border-slate-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Additional Information */}
            <div className="bg-white p-6 rounded-lg shadow-lg border border-slate-200">
              <h2 className="text-xl font-semibold mb-6">Additional Information</h2>
              <div className="space-y-6">
                <div>
                  <label htmlFor="cover_letter" className="block text-sm font-medium text-slate-700 mb-1">
                    Cover Letter *
                  </label>
                  <textarea
                    id="cover_letter"
                    name="cover_letter"
                    required
                    rows={6}
                    value={formData.cover_letter}
                    onChange={handleChange}
                    placeholder="Tell us why you'd be a great fit for this role..."
                    className="w-full rounded-md border-slate-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label htmlFor="resume_link" className="block text-sm font-medium text-slate-700 mb-1">
                    Resume/CV Link (Google Drive/Dropbox) *
                  </label>
                  <input
                    type="url"
                    id="resume_link"
                    name="resume_link"
                    required
                    value={formData.resume_link}
                    onChange={handleChange}
                    placeholder="https://drive.google.com/file/d/..."
                    className="w-full rounded-md border-slate-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                  <p className="mt-2 text-sm text-slate-500">
                    Please upload your resume to Google Drive or Dropbox and share the link here
                  </p>
                </div>
                <div>
                  <label htmlFor="availability" className="block text-sm font-medium text-slate-700 mb-1">
                    When can you start? *
                  </label>
                  <select
                    id="availability"
                    name="availability"
                    required
                    value={formData.availability}
                    onChange={handleChange}
                    className="w-full rounded-md border-slate-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  >
                    <option value="">Select availability</option>
                    <option value="immediately">Immediately</option>
                    <option value="2-weeks">2 weeks notice</option>
                    <option value="1-month">1 month notice</option>
                    <option value="2-months">2+ months notice</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="salary" className="block text-sm font-medium text-slate-700 mb-1">
                    Expected Salary (₹ per annum)
                  </label>
                  <input
                    type="text"
                    id="salary"
                    name="salary"
                    value={formData.salary}
                    onChange={handleChange}
                    placeholder="e.g., 12,00,000"
                    className="w-full rounded-md border-slate-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label htmlFor="referral" className="block text-sm font-medium text-slate-700 mb-1">
                    How did you hear about us?
                  </label>
                  <input
                    type="text"
                    id="referral"
                    name="referral"
                    value={formData.referral}
                    onChange={handleChange}
                    className="w-full rounded-md border-slate-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={isSubmitting}
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="animate-spin -ml-1 mr-3 h-5 w-5" />
                    Submitting...
                  </>
                ) : (
                  'Submit Application'
                )}
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Apply;